import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Shield } from "lucide-react";
import adminApi, { apiError } from "@/lib/adminApi";

const inputClass =
  "bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-gold outline-none w-full";

const EMPTY = { name: "", display_order: 0, is_active: true };

const AdminClubs = () => {
  const [clubs, setClubs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => adminApi.get("/clubs").then(({ data }) => setClubs(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Le nom du club est obligatoire.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) await adminApi.put(`/clubs/${editingId}`, form);
      else await adminApi.post("/clubs", form);
      toast.success(editingId ? "Club mis à jour." : "Club ajouté.");
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await adminApi.put(`/clubs/${c.id}`, { name: c.name, display_order: c.display_order, is_active: !c.is_active });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (c) => {
    try {
      await adminApi.delete(`/clubs/${c.id}`);
      toast.success("Club supprimé.");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="admin-clubs" className="space-y-6">
      <h1 className="font-display uppercase text-4xl tracking-wide">Clubs — Ils nous font confiance</h1>
      <p className="text-sm text-white/50">
        Ces clubs apparaissent sur la page d'accueil, section « Ils nous font confiance ».
      </p>

      <form onSubmit={submit} data-testid="club-form" className="bg-noir-surface border border-noir-border p-6 space-y-4">
        <h2 className="font-display uppercase text-xl tracking-wide text-gold">
          {editingId ? "Modifier le club" : "Ajouter un club"}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <input data-testid="club-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom du club * (ex : Marseille)" className={inputClass} />
          <input data-testid="club-order" type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) })} placeholder="Ordre d'affichage" className={inputClass} />
          <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer">
            <input data-testid="club-active" type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" />
            Actif (visible publiquement)
          </label>
        </div>
        <div className="flex gap-3">
          <button data-testid="club-submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {editingId ? "Enregistrer" : "Ajouter le club"}
          </button>
          {editingId && (
            <button type="button" data-testid="club-cancel-edit" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="px-6 py-3 border border-white/20 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:border-white">
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="space-y-3">
        {clubs.map((c) => (
          <div key={c.id} data-testid={`club-row-${c.id}`} className="bg-noir-surface border border-noir-border p-4 flex flex-wrap items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center border border-gold/30 bg-black/40">
              <Shield size={16} className="text-gold" />
            </div>
            <div className="flex-1 min-w-[140px]">
              <p className="font-semibold text-sm">{c.name}</p>
              <p className="text-xs text-white/40">Ordre : {c.display_order}</p>
            </div>
            <button
              data-testid={`club-toggle-${c.id}`}
              onClick={() => toggleActive(c)}
              className={`text-[10px] font-bold uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${
                c.is_active ? "text-gold border-gold/40" : "text-white/40 border-white/15"
              }`}
            >
              {c.is_active ? "Actif" : "Inactif"}
            </button>
            <button
              data-testid={`club-edit-${c.id}`}
              onClick={() => { setEditingId(c.id); setForm({ name: c.name, display_order: c.display_order, is_active: c.is_active }); window.scrollTo({ top: 0 }); }}
              className="px-4 py-2 border border-white/15 text-white/60 hover:border-gold hover:text-gold text-xs font-bold uppercase tracking-wider transition-colors"
            >
              Modifier
            </button>
            <button data-testid={`club-delete-${c.id}`} onClick={() => setConfirmDelete(c)} className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/60 hover:border-rouge hover:text-rouge-hover transition-colors">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {clubs.length === 0 && <p className="text-white/40 text-sm">Aucun club pour le moment.</p>}
      </div>

      {confirmDelete && (
        <div data-testid="club-delete-confirm" className="fixed inset-0 z-50 bg-noir/90 backdrop-blur flex items-center justify-center p-6">
          <div className="bg-noir-surface border border-rouge/40 p-8 max-w-md w-full">
            <h2 className="font-display uppercase text-2xl tracking-wide text-rouge-hover">Supprimer ce club ?</h2>
            <p className="mt-3 text-white/60 text-sm">Voulez-vous vraiment supprimer <strong className="text-white">{confirmDelete.name}</strong> ? Il disparaîtra de la page d'accueil.</p>
            <div className="mt-6 flex gap-3">
              <button data-testid="club-delete-yes" onClick={() => remove(confirmDelete)} className="px-6 py-3 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover">
                Supprimer
              </button>
              <button data-testid="club-delete-no" onClick={() => setConfirmDelete(null)} className="px-6 py-3 border border-white/20 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:border-white">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClubs;
